/**
 * Release-notes email allowlist — client-side helper.
 *
 * Baseline mirrors the server-side allowlist in
 * server/routes/non_generated_single_table_business_logic_routes/release-notes-business-logic.ts
 * (which itself is ported from bjjcomptracker's applications_monorepo/
 * bjj_api_server/src/env.ts parseAdminAllowlist).
 *
 * The client copy exists so we can hide the sidebar menu item and redirect
 * off `/devtools/release-notes` without a network round-trip. It is **not**
 * the source of truth — the server-side allowlist is. If the two drift,
 * the client is wrong; the server still enforces correctly. Users see a
 * bad UX moment, not a security hole.
 *
 * To override for a local dev, set `VITE_RELEASE_NOTES_ADMIN_EMAIL_ALLOWLIST`
 * (comma-separated) in your .env.local. Same syntax as the server env
 * (full emails or `@domain.tld` globs). When set, it replaces the baseline
 * entirely — include the baseline entries in your override if you want to
 * keep them.
 */

// zztest+admin@lithos.test = the ZZ-TEST admin login AI browser testers use
// (docs/test-accounts.md). Mirrors the server baseline.
const BASELINE_EMAILS = [
  'aigtestinging@gmail.com',
  'aigeniustesting@gmail.com',
  'zztest+admin@lithos.test',
];
const BASELINE_DOMAINS = ['aigeniusconsulting.com', 'aigeniusautomations.com'];

interface Allowlist {
  emails: Set<string>;
  domains: Set<string>;
}

let cached: Allowlist | null = null;

function parseAllowlist(): Allowlist {
  if (cached) return cached;

  const raw = (import.meta.env.VITE_RELEASE_NOTES_ADMIN_EMAIL_ALLOWLIST as string | undefined) ?? '';
  const entries = raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

  const emails = new Set<string>();
  const domains = new Set<string>();

  const consume = (entry: string): void => {
    if (entry.startsWith('@')) {
      const d = entry.slice(1);
      if (d) domains.add(d);
      return;
    }
    if (entry.includes('@')) emails.add(entry);
  };

  if (entries.length > 0) {
    entries.forEach(consume);
  } else {
    BASELINE_EMAILS.forEach((e) => emails.add(e));
    BASELINE_DOMAINS.forEach((d) => domains.add(d));
    if (import.meta.env.DEV) emails.add('dev@fallback.local');
  }

  cached = { emails, domains };
  return cached;
}

/**
 * Returns true if `email` is in the release-notes admin allowlist.
 * Case-insensitive, trimmed.
 */
export function isReleaseNotesEmailAllowlisted(email: string | null | undefined): boolean {
  if (!email) return false;
  const normalized = email.trim().toLowerCase();
  if (!normalized) return false;
  const { emails, domains } = parseAllowlist();
  if (emails.has(normalized)) return true;
  const atIdx = normalized.indexOf('@');
  if (atIdx < 0) return false;
  return domains.has(normalized.slice(atIdx + 1));
}
